/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
    AfterViewInit,
    ElementRef,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { debounceTime, distinctUntilChanged, switchMap, tap, finalize } from 'rxjs/operators';
import { EMPTY, Observable } from 'rxjs';
import { DEFAULT_PAGE_SIZE } from '../../../constants/pagination.const';

@Component({
    selector: 'app-search-select-autocomplete',
    standalone: true,
    imports: [ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule],
    templateUrl: './search-select-autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './search-select-autocomplete.component.scss',
})
export class SearchSelectAutoCompleteComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() label = '';
    @Input() valueKey = 'id';
    @Input() displayKey = 'name';
    @Input() selectedOption!: any;

    @Input() loadFn!: (params: {
        pageNumber: number;
        pageSize: number;
        search?: string;
    }) => Observable<{ items: any[]; hasMore: boolean; totalCount: number }>;

    @Output() selectionChange = new EventEmitter<any>();

    @ViewChild(MatSelect) matSelect!: MatSelect;
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

    control = new FormControl();
    searchControl = new FormControl('');

    options: any[] = [];
    private pendingReset = false;

    private pageNumber = 1;
    private readonly pageSize = DEFAULT_PAGE_SIZE;
    private hasMore = true;
    public loading = false;
    public keywordLoading = false;

    ngOnInit() {
        this.pendingReset = true;
        this.loadPage()?.subscribe();

        this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                tap(() => {
                    this.pageNumber = 1;
                    this.hasMore = true;
                    this.pendingReset = true;
                }),
                switchMap((search) => this.loadPage(search ?? '', true))
            )
            .subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedOption']?.currentValue) {
            const value = changes['selectedOption'].currentValue;
            const key = value?.[this.valueKey]?.toString();

            if (!key) return;

            this.control.setValue(value, { emitEvent: false });
            this.selectedOption = value;
        }
    }

    ngAfterViewInit() {
        this.matSelect.openedChange.subscribe((open) => {
            if (!open) return;

            setTimeout(() => {
                const panel =
                    document.querySelector('.cdk-overlay-pane .mat-mdc-select-panel') ??
                    document.querySelector('.cdk-overlay-pane .mat-select-panel');

                if (!panel) return;

                const scrollContainer = this.findScrollContainer(panel as HTMLElement);

                if (!scrollContainer) {
                    console.warn('No scroll container found');
                    return;
                }

                scrollContainer.addEventListener('scroll', this.onScroll.bind(this));

                this.searchInput?.nativeElement?.focus();
            }, 0);
        });
    }

    private findScrollContainer(el: HTMLElement): HTMLElement | null {
        let node: HTMLElement | null = el;

        while (node) {
            const overflowY = getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return node;
            }
            node = node.parentElement;
        }

        return null;
    }

    private loadPage(search?: string, keywordSearch?: boolean): Observable<{ items: any[]; hasMore: boolean; totalCount: number }> {
        if (this.loading || !this.hasMore) {
            return EMPTY;
        }

        if (keywordSearch) {
            this.keywordLoading = true;
        } else {
            this.loading = true;
        }

        return this.loadFn({
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            search,
        }).pipe(
            tap((res) => {
                if (this.pendingReset) {
                    this.options = [];

                    this.pendingReset = false;
                    if (this.selectedOption) {
                        this.options = [this.selectedOption];
                    }
                }

                this.options.push(...res.items.filter((item) => item[this.valueKey] !== this.selectedOption[this.valueKey]));
                this.hasMore = this.options.length < res.totalCount;
                this.pageNumber++;

                this.tryPreselect();
            }),
            finalize(() => {
                if (keywordSearch) {
                    this.keywordLoading = false;
                } else {
                    this.loading = false;
                }
            })
        );
    }

    private tryPreselect() {
        if (!this.selectedOption[this.valueKey] || this.control.value) return;

        if (this.selectedOption) {
            this.control.setValue(this.selectedOption);
        }
    }

    // Custom clear button for the search field above - the native
    // type="search" clear icon is hidden globally (see .filter-bar__search
    // in _filters.scss) since it can't be restyled to match the app.
    onClearSearch(): void {
        this.searchControl.setValue('');
        this.searchInput?.nativeElement.focus();
    }

    onScroll(e: any) {
        const el = e.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;

        if (nearBottom && !this.loading && this.options.length > this.pageSize - 1) {
            this.pendingReset = false;
            this.loadPage(this.searchControl.value ?? '')?.subscribe();
        }
    }

    onSelectionChange(value: any) {
        this.selectedOption = value;

        const selectedKey = value?.[this.valueKey]?.toString();
        const displayKey = this.displayKey;

        if (!selectedKey) {
            this.selectionChange.emit(value);
            return;
        }

        const rest = this.options.filter((o) => o?.[this.valueKey]?.toString() !== selectedKey);

        rest.sort((a, b) => {
            const av = (a?.[displayKey] ?? '').toString().toLowerCase();
            const bv = (b?.[displayKey] ?? '').toString().toLowerCase();
            return av.localeCompare(bv);
        });

        this.options = [value, ...rest];

        this.selectionChange.emit(value);
    }

    getDisplay(option: any) {
        return option?.[this.displayKey];
    }

    compareByValueKey = (a: any, b: any): boolean => {
        if (!a || !b) return false;
        return a[this.valueKey]?.toString() === b[this.valueKey]?.toString();
    };
}
